import React, { useEffect, useMemo, useState } from "react";
import { formatRupiah } from "../../utils/formatPrice";

export type ConsultationStage = "initial" | "payment" | "final-confirm";

interface ConsultationActionCardProps {
  stage: ConsultationStage;
  doctorName: string;
  doctorPhotoUrl?: string;
  price?: number;
  paymentDeadline?: string | null;
  acceptanceDeadline?: string | null;
  serverTime?: string | null;
  paymentVerifying?: boolean;
  onAction: () => void;
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, "0");
  const secs = (seconds % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

const ConsultationActionCard: React.FC<ConsultationActionCardProps> = ({
  stage,
  doctorName,
  doctorPhotoUrl,
  price,
  paymentDeadline,
  acceptanceDeadline,
  serverTime,
  paymentVerifying = false,
  onAction,
}) => {
  const [remaining, setRemaining] = useState<number | null>(null);

  const clockOffsetMs = useMemo(() => {
    if (!serverTime) return 0;
    const parsed = new Date(serverTime).getTime();
    return Number.isNaN(parsed) ? 0 : parsed - Date.now();
  }, [serverTime]);

  const deadline = paymentVerifying
    ? null
    : stage === "payment" ? paymentDeadline : stage === "initial" ? acceptanceDeadline : null;

  useEffect(() => {
    if (!deadline) {
      setRemaining(null);
      return;
    }

    const deadlineMs = new Date(deadline).getTime();
    if (Number.isNaN(deadlineMs)) {
      setRemaining(null);
      return;
    }

    const tick = () => {
      setRemaining(Math.max(0, Math.floor((deadlineMs - (Date.now() + clockOffsetMs)) / 1000)));
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [deadline, clockOffsetMs]);

  const expired = remaining !== null && remaining <= 0;

  const content = {
    payment: {
      title: "Menunggu Pembayaran",
      subtitle: formatRupiah(price ?? 0),
      button: "Bayar",
      timerLabel: (s: number) => `Selesaikan dalam ${formatTime(s)}`,
      expiredLabel: "Waktu pembayaran habis",
    },
    initial: {
      title: "Menunggu Konfirmasi Dokter",
      subtitle: "Menunggu dokter menerima permintaan konsultasi Anda",
      button: "Lihat",
      timerLabel: (s: number) => `Sisa waktu ${formatTime(s)}`,
      expiredLabel: "Dokter belum merespons",
    },
    "final-confirm": {
      title: "Menunggu Dokter Memulai",
      subtitle: "Pembayaran berhasil, dokter akan segera memulai konsultasi",
      button: "Lihat",
      timerLabel: (s: number) => formatTime(s),
      expiredLabel: "",
    },
  }[stage];

  return (
    <div className="mb-4">
      <h3 className="text-md font-semibold mb-2">{content.title}</h3>

      <div className="bg-white rounded-xl shadow p-4 flex items-center gap-3">
        <img
          src={doctorPhotoUrl || "https://i.pravatar.cc/100?img=2"}
          alt={doctorName}
          className="w-12 h-12 rounded-full object-cover flex-shrink-0"
          onError={(e) => {
            e.currentTarget.src = "https://m.linksehat.com/assets/img/users/dummy.jpg";
          }}
        />

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-gray-800 truncate">{doctorName}</p>
          <p className="text-sm text-gray-500">{content.subtitle}</p>

          {paymentVerifying && (
            <p className="text-xs font-medium mt-1 text-teal-600">Memverifikasi pembayaran...</p>
          )}

          {remaining !== null && (
            <p className={`text-xs font-medium mt-1 ${expired ? "text-red-500" : "text-orange-500"}`}>
              {expired ? content.expiredLabel : content.timerLabel(remaining)}
            </p>
          )}
        </div>

        <button
          onClick={onAction}
          disabled={stage === "payment" && expired}
          className="bg-teal-500 disabled:bg-gray-300 text-white text-sm font-semibold px-5 py-2 rounded-full flex-shrink-0"
        >
          {content.button}
        </button>
      </div>
    </div>
  );
};

export default ConsultationActionCard;
